import { Button, Link, Logo, PageLayout } from "@repo/ping-design";
import "./ErrorPage.scss";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

const Pills = () => {
  return (
    <div className="ErrorPage__Pills">
      <svg
        viewBox="0 0 1936 1096"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMinYMin none"
      >
        <g>
          <rect
            x="1316.28"
            y="33.2778"
            width="411.464"
            height="330.96"
            rx="165.48"
            stroke="#F32C01"
            strokeWidth="2.55568"
          />
          <rect
            x="586.278"
            y="1019.28"
            width="812.444"
            height="246.444"
            rx="123.222"
            stroke="#F32C01"
            strokeWidth="2.55568"
          />
          <rect
            x="303.278"
            y="255.278"
            width="184.009"
            height="306.681"
            rx="92.0044"
            stroke="#FFAC4D"
            strokeWidth="2.55568"
          />
          <rect
            x="85.2778"
            y="957.278"
            width="363.444"
            height="222.444"
            rx="111.222"
            stroke="#FFAC4D"
            strokeWidth="2.55568"
          />
          <rect
            x="998.278"
            y="718.278"
            width="498.444"
            height="232.444"
            rx="116.222"
            stroke="#FFAC4D"
            strokeWidth="2.55568"
          />
          <rect
            x="467.278"
            y="216.722"
            width="183.444"
            height="755.444"
            rx="91.7221"
            transform="rotate(-90 467.278 216.722)"
            stroke="#C94DFF"
            strokeWidth="2.55568"
          />
          <rect
            x="1670.28"
            y="745.722"
            width="323.444"
            height="226.444"
            rx="113.222"
            transform="rotate(-90 1670.28 745.722)"
            stroke="#C94DFF"
            strokeWidth="2.55568"
          />
          <rect
            x="-50.7222"
            y="715.722"
            width="323.444"
            height="226.444"
            rx="113.222"
            transform="rotate(-90 -50.7222 715.722)"
            stroke="#C94DFF"
            strokeWidth="2.55568"
          />
          <rect
            x="272.278"
            y="633.278"
            width="369.444"
            height="203.444"
            rx="101.722"
            stroke="#3895FF"
            strokeWidth="2.55568"
          />
          <rect
            x="36.2778"
            y="17.2778"
            width="212.444"
            height="290.444"
            rx="106.222"
            stroke="#3895FF"
            strokeWidth="2.55568"
          />
          <rect
            x="1603.28"
            y="889.278"
            width="323.444"
            height="290.444"
            rx="145.222"
            stroke="#3895FF"
            strokeWidth="2.55568"
          />
        </g>
        <defs>
          <filter
            id="filter0_dd_1008_58782"
            x="-12"
            y="-16"
            width="1960"
            height="1120"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="4" />
            <feGaussianBlur stdDeviation="4" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.06 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_1008_58782"
            />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset />
            <feGaussianBlur stdDeviation="2" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0"
            />
            <feBlend
              mode="normal"
              in2="effect1_dropShadow_1008_58782"
              result="effect2_dropShadow_1008_58782"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect2_dropShadow_1008_58782"
              result="shape"
            />
          </filter>
          <clipPath id="clip0_1008_58782">
            <rect
              width="1920"
              height="1080"
              fill="white"
              transform="translate(8 4)"
            />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
};

type ErrorPageProps = {
  mainText?: string;
  type: "ERROR" | "NOT_FOUND" | "NETWORK_ERROR" | "CUSTOM";
  children?: React.ReactNode;
  buttonLabel?: string;
  onClick?: () => void;
  inline?: boolean;
};

const ErrorPage = ({
  type,
  mainText,
  children,
  buttonLabel,
  onClick,
  inline = false,
}: ErrorPageProps) => {
  const navigate = useNavigate();

  const middleSection = useMemo(() => {
    if (type === "ERROR") {
      return (
        <div className="ErrorPage__SecondaryText">
          An error has occurred. We apologize for the inconvenience. If the
          issue continues, please reach out to our{" "}
          <Link
            className="ErrorPage__SecondaryText__Link"
            variant="brand"
            href="mailto:support@pingintel.com"
          >
            support team
          </Link>
        </div>
      );
    } else if (type === "NOT_FOUND") {
      return (
        <div className="ErrorPage__SecondaryText">
          The page you are looking for does not exist
        </div>
      );
    } else if (type === "NETWORK_ERROR") {
      return (
        <div className="ErrorPage__SecondaryText">
          The page you are looking for does not exist
        </div>
      );
    } else if (type === "CUSTOM") {
      return <div className="ErrorPage__SecondaryText">{children}</div>;
    } else {
      return (
        <div className="ErrorPage__SecondaryText">
          An error has occurred. We apologize for the inconvenience. If the
          issue continues, please reach out to our{" "}
          <Link
            className="ErrorPage__SecondaryText__Link"
            variant="brand"
            href="mailto:support@pingintel.com"
          >
            support team
          </Link>
        </div>
      );
    }
  }, [type, children]);

  const buttonSection = useMemo(() => {
    if (type === "ERROR") {
      return null;
    } else if (type === "NOT_FOUND") {
      return (
        <Button
          className="ErrorPage__Button"
          label="Go Back"
          onClick={() => navigate("/")}
          iconName="ArrowLeft"
          variant="inverse"
        />
      );
    } else if (type === "NETWORK_ERROR") {
      return (
        <Button
          className="ErrorPage__Button"
          label="Reload"
          onClick={() => window.location.reload()}
          variant="inverse"
        />
      );
    } else if (type === "CUSTOM") {
      return onClick ? (
        <Button
          className="ErrorPage__Button"
          label={buttonLabel}
          onClick={onClick}
          variant="inverse"
        />
      ) : null;
    } else {
      return null;
    }
  }, [buttonLabel, history, type]);

  const titleSection = useMemo(() => {
    if (type === "ERROR") {
      return <div className="ErrorPage__MainText">Something went wrong!</div>;
    } else if (type === "NOT_FOUND") {
      return <div className="ErrorPage__MainText">Not found!</div>;
    } else if (type === "NETWORK_ERROR") {
      return <div className="ErrorPage__MainText">Network error</div>;
    } else if (type === "CUSTOM") {
      return <div className="ErrorPage__MainText">{mainText}</div>;
    } else {
      return <div className="ErrorPage__MainText">Something went wrong!</div>;
    }
  }, [mainText, type]);

  const pageTitle = useMemo(() => {
    if (type === "ERROR") {
      return "Error";
    } else if (type === "NOT_FOUND") {
      return "Not Found";
    } else if (type === "NETWORK_ERROR") {
      return "Network Error";
    } else if (type === "CUSTOM") {
      return mainText || "Error";
    } else {
      return "Error";
    }
  }, [type, mainText]);

  const errorContent = (
    <div className="ErrorPage">
      <Pills />
      <div className="ErrorPage__MiddleContainer">
        <Logo className="ErrorPage__Logo" />
        {titleSection}
        {middleSection}
        {buttonSection}
      </div>
    </div>
  );

  if (inline) {
    return errorContent;
  }

  return <PageLayout title={pageTitle}>{errorContent}</PageLayout>;
};

export { ErrorPage };
